import { canViewSpeculationSettlements } from "./speculationSettlementsAccess";
import {
  getFunderTabPath,
  type WorkspaceTab,
} from "@client/utils/funderTabMemory";
import {
  type NavItemVisibility,
  type NavVisibility,
} from "@client/utils/navPhaseVisibility";

// Declarative config for the round's primary nav (the top-bar links in the
// SiteHeader): which entries exist, in what order, who sees them, and where
// their phase visibility comes from. The SiteHeader maps these defs onto
// AppBar items and wires up the visited/unseen dots; presence, order, and
// structure live here. Mirrors `roundAdminNav.ts`, the same pattern for the
// admin gear menu.

export type RoundNavLinkDef = {
  key: string;
  label: string;
  href: string;
  // Path prefix used to compute the active/highlighted state. Defaults to
  // `href`. Set this when the link points to a default sub-page (e.g.
  // `/funder/review/overview`) but the tab should stay highlighted across
  // a broader section (e.g. any `/funder/review/...` route).
  activePath?: string;
  visibility: NavItemVisibility;
  // Start in the AppBar's "More" overflow instead of inline (role-based
  // de-emphasis; the AppBar still shows the item inline while it's active).
  defaultFolded?: boolean;
};

export type RoundNavEntry =
  | ({ kind: "link" } & RoundNavLinkDef)
  | {
      kind: "dropdown";
      key: string;
      label: string;
      activePath: string;
      items: RoundNavLinkDef[];
      defaultFolded?: boolean;
    };

type PhaseGatedLinkDef = {
  key: string;
  label: string;
  // Round-relative path; the builder prefixes `/round/:roundId`.
  path: string;
  pickVisibility: (v: NavVisibility) => NavItemVisibility;
};

const SETUP_LINKS: PhaseGatedLinkDef[] = [
  {
    key: "setup:onboarding",
    label: "Onboarding",
    path: "/onboarding",
    pickVisibility: (v) => v.setup.onboarding,
  },
  {
    key: "setup:recusals",
    label: "Recusals",
    path: "/recusals",
    pickVisibility: (v) => v.setup.recusals,
  },
  {
    key: "setup:investigations",
    label: "Investigations",
    path: "/investigations",
    pickVisibility: (v) => v.setup.investigations,
  },
];

const PRIMARY_LINKS: PhaseGatedLinkDef[] = [
  {
    key: "proposals",
    label: "Proposals",
    path: "/proposals",
    pickVisibility: (v) => v.proposals,
  },
  {
    key: "mvfs",
    label: "MVFs",
    path: "/comparisons",
    pickVisibility: (v) => v.mvfs,
  },
  {
    key: "disagreements",
    label: "Disagreements",
    path: "/disagreements",
    pickVisibility: (v) => v.disagreements,
  },
  {
    key: "funding-flow",
    label: "Funding Flow",
    path: "/funding-flow",
    pickVisibility: (v) => v.analysis,
  },
];

// The funder workspace tabs. Shown only to funders and SFC admins, always
// active regardless of phase.
const FUNDER_TAB_LINKS: { key: string; label: string; tab: WorkspaceTab }[] = [
  { key: "funder-review", label: "Review", tab: "review" },
  { key: "funder-decide", label: "Decide", tab: "decide" },
];

export type RoundPrimaryNavContext = {
  actingAsUserId: string;
  roundId: string;
  visibility: NavVisibility;
  isSfcAdmin: boolean;
  isSfcRound: boolean;
  hasApplicationBuilderForms: boolean;
  isRoundAdmin: boolean;
  isFunder: boolean;
  // Restricted funders have no Review workspace — their Review tab is
  // dropped (it must not be discoverable from Decide) and they get their
  // own layout: Proposals, MVFs, Disagreements, Decide, and Funding Flow
  // inline, with the Setup pages as flat links in the "More" overflow.
  isRestrictedFunder: boolean;
  // Current browser location, used so the funder tab you're already on links
  // to the current page instead of the remembered sub-path.
  location: string;
  currentFunderTab: WorkspaceTab | null;
};

// Builds the whole top-bar nav for a round, in display order.
//
// Standard layout (recommenders, admins): Setup dropdown, phase-gated links,
// then the funder tabs for funders and SFC admins.
//
// Funder layout (funders who aren't round admins or SFC admins): their
// workspace tabs (Review, Decide) lead, and everything else starts in the
// AppBar's "More" overflow — Setup nests there as a submenu. Admins keep the
// full layout even when they're also funders: they work across the whole nav.
//
// Restricted funder layout: with no Review workspace, Decide alone is too
// thin to lead the nav, so the phase-gated links stay inline — Proposals,
// MVFs, Disagreements, Decide, Funding Flow — and the Setup pages
// (Onboarding, Recusals, Investigations) sit flat in the "More" overflow,
// not nested under a Setup dropdown.
export function buildRoundNavEntries(
  ctx: RoundPrimaryNavContext,
): RoundNavEntry[] {
  const base = `/round/${ctx.roundId}`;
  const funderLayout = ctx.isFunder && !ctx.isRoundAdmin && !ctx.isSfcAdmin;
  const restrictedLayout = funderLayout && ctx.isRestrictedFunder;

  const resolve = (def: PhaseGatedLinkDef): RoundNavLinkDef => ({
    key: def.key,
    label: def.label,
    href: `${base}${def.path}`,
    visibility: def.pickVisibility(ctx.visibility),
  });

  const setupEntry: RoundNavEntry = {
    kind: "dropdown",
    key: "setup",
    label: "Setup",
    activePath: `${base}/setup`,
    items: SETUP_LINKS.map(resolve),
    ...(funderLayout && { defaultFolded: true }),
  };

  const phaseGated: RoundNavEntry[] = PRIMARY_LINKS.map((def) => ({
    kind: "link" as const,
    ...resolve(def),
    ...(funderLayout && !restrictedLayout && { defaultFolded: true }),
  }));

  const funderTabs: RoundNavEntry[] =
    ctx.isSfcAdmin || ctx.isFunder
      ? FUNDER_TAB_LINKS.filter(
          ({ tab }) => !(ctx.isRestrictedFunder && tab === "review"),
        ).map(({ key, label, tab }) => {
          const suffix =
            ctx.currentFunderTab === tab
              ? ctx.location.slice(`${base}/funder/`.length)
              : getFunderTabPath(ctx.roundId, tab);
          // Remembered and current URLs must be revalidated for the viewer:
          // tab memory survives impersonation and permission changes.
          const deniedSettlements =
            tab === "decide" &&
            suffix.split(/[/?#]/)[1] === "settlements" &&
            !canViewSpeculationSettlements(
              ctx.actingAsUserId,
              ctx.isSfcAdmin || ctx.isRoundAdmin,
            );
          return {
            kind: "link" as const,
            key,
            label,
            href: `${base}/funder/${deniedSettlements ? "decide/incremental-evals" : suffix}`,
            activePath: `${base}/funder/${tab}`,
            visibility: { state: "active" as const },
          };
        })
      : [];

  if (restrictedLayout) {
    const fundingFlow = phaseGated.filter((e) => e.key === "funding-flow");
    const rest = phaseGated.filter((e) => e.key !== "funding-flow");
    const setupFlat: RoundNavEntry[] = SETUP_LINKS.map((def) => ({
      kind: "link" as const,
      ...resolve(def),
      defaultFolded: true,
    }));
    return [...rest, ...funderTabs, ...fundingFlow, ...setupFlat];
  }
  return funderLayout
    ? [...funderTabs, setupEntry, ...phaseGated]
    : [
        setupEntry,
        ...(ctx.isSfcRound &&
        ctx.hasApplicationBuilderForms &&
        (ctx.isSfcAdmin || ctx.isRoundAdmin)
          ? [
              {
                kind: "link" as const,
                key: "applications",
                label: "Applications",
                href: `${base}/applications/responses`,
                activePath: `${base}/applications`,
                visibility: { state: "active" as const },
              },
            ]
          : []),
        ...phaseGated,
        ...funderTabs,
      ];
}
